import type { MailPlaceholderValueType } from "api/mailTemplates/MailTemplate.typed";
import {
  MAIL_PLACEHOLDER_TYPE_EMAIL,
  MAIL_PLACEHOLDER_TYPE_PHONE,
  MAIL_PLACEHOLDER_TYPE_STRING,
  MAIL_PLACEHOLDER_TYPE_URL,
} from "api/mailTemplates/MailTemplate.typed";
import { escapeHtmlAttr } from "./mailLineParts";
import { mailContactIconHtml } from "./mailEmailSafeIcons";

export const MAIL_CONTACT_KINDS = [
  "phone",
  "email",
  "website",
  "facebook",
  "instagram",
  "linkedin",
  "youtube",
  "whatsapp",
] as const;

export type MailContactKind = (typeof MAIL_CONTACT_KINDS)[number];

export type MailContactItem = {
  id: string;
  kind: MailContactKind;
  enabled: boolean;
  value: string;
};

export type MailContactBlock = {
  id: string;
  type: "contact";
  items: MailContactItem[];
};

export const MAIL_CONTACT_KIND_LABELS: Record<MailContactKind, string> = {
  phone: "Téléphone",
  email: "E-mail",
  website: "Site web",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  whatsapp: "WhatsApp",
};

export const MAIL_CONTACT_FIELD_PREFIX = "contact:" as const;

const GO_PH_TOKEN_RE = /^\{\{\.[A-Za-z0-9_]+\}\}$/;

const DEFAULT_ENABLED_KINDS: ReadonlySet<MailContactKind> = new Set(["phone", "email"]);

export function isMailContactKind(raw: string): raw is MailContactKind {
  return (MAIL_CONTACT_KINDS as readonly string[]).includes(raw);
}

export function newContactItemId(): string {
  return `ci-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function mailContactItemFieldKey(kind: MailContactKind): string {
  return `${MAIL_CONTACT_FIELD_PREFIX}${kind}`;
}

export function parseMailContactItemFieldKey(field: string): MailContactKind | null {
  if (!field.startsWith(MAIL_CONTACT_FIELD_PREFIX)) {
    return null;
  }

  const raw = field.slice(MAIL_CONTACT_FIELD_PREFIX.length);

  return isMailContactKind(raw) ? raw : null;
}

export function ensureMailContactItems(existing: readonly MailContactItem[]): MailContactItem[] {
  const byKind = new Map<MailContactKind, MailContactItem>();

  for (const item of existing) {
    if (!byKind.has(item.kind)) {
      byKind.set(item.kind, item);
    }
  }

  return MAIL_CONTACT_KINDS.map((kind) => {
    const found = byKind.get(kind);

    if (found) {
      return {
        id: found.id || newContactItemId(),
        kind,
        enabled: found.enabled !== false,
        value: found.value,
      };
    }

    return {
      id: newContactItemId(),
      kind,
      enabled: false,
      value: "",
    };
  });
}

export function createEmptyMailContactBlock(blockId: string): MailContactBlock {
  return {
    id: blockId,
    type: "contact",
    items: MAIL_CONTACT_KINDS.map((kind) => ({
      id: newContactItemId(),
      kind,
      enabled: DEFAULT_ENABLED_KINDS.has(kind),
      value: "",
    })),
  };
}

export function mailPlaceholderValueTypesForContactKind(
  kind: MailContactKind,
): ReadonlySet<MailPlaceholderValueType> {
  switch (kind) {
    case "phone":
    case "email":
    case "website":
    case "facebook":
    case "instagram":
    case "linkedin":
    case "youtube":
    case "whatsapp":
      return new Set([
        MAIL_PLACEHOLDER_TYPE_PHONE,
        MAIL_PLACEHOLDER_TYPE_EMAIL,
        MAIL_PLACEHOLDER_TYPE_URL,
        MAIL_PLACEHOLDER_TYPE_STRING,
      ]);
  }
}

function isGoPlaceholderToken(raw: string): boolean {
  return GO_PH_TOKEN_RE.test(raw.trim());
}

function hasUrlScheme(raw: string): boolean {
  const lower = raw.trim().toLowerCase();

  return (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:")
  );
}

export function mailContactHref(kind: MailContactKind, value: string): string {
  const raw = value.trim();

  if (!raw) {
    return "";
  }

  const lower = raw.toLowerCase();

  switch (kind) {
    case "phone":
      if (lower.startsWith("tel:")) {
        return raw;
      }

      return `tel:${raw}`;
    case "email":
      if (lower.startsWith("mailto:")) {
        return raw;
      }

      return `mailto:${raw}`;
    case "whatsapp":
      if (hasUrlScheme(raw) || isGoPlaceholderToken(raw)) {
        if (isGoPlaceholderToken(raw) && !hasUrlScheme(raw)) {
          return `https://wa.me/${raw}`;
        }

        return raw;
      }

      return `https://wa.me/${raw.replace(/^\+/, "")}`;
    default:
      if (hasUrlScheme(raw) || isGoPlaceholderToken(raw)) {
        return raw;
      }

      return `https://${raw}`;
  }
}

export function serializeMailContactBlock(block: MailContactBlock): string {
  const cells = ensureMailContactItems(block.items)
    .filter((item) => item.enabled)
    .map((item) => {
      const href = mailContactHref(item.kind, item.value);
      const kindEsc = escapeHtmlAttr(item.kind);
      const valueEsc = escapeHtmlAttr(item.value);
      const icon = mailContactIconHtml(item.kind, MAIL_CONTACT_KIND_LABELS[item.kind]);
      const inner = href
        ? `<a href="${escapeHtmlAttr(href)}" style="text-decoration:none;color:#111827;display:block;" target="_blank" rel="noopener noreferrer">${icon}</a>`
        : icon;

      return `<td data-mail-contact-kind="${kindEsc}" data-mail-contact-value="${valueEsc}" align="center" valign="middle" style="padding:0 10px;">${inner}</td>`;
    })
    .join("");

  return `<table role="presentation" data-mail-contact="1" cellpadding="0" cellspacing="0" align="center" style="border-collapse:collapse;margin:16px auto;"><tr>${cells}</tr></table>`;
}

function valueFromStoredHref(kind: MailContactKind, href: string, attrValue: string): string {
  const fromAttr = attrValue.trim();

  if (fromAttr) {
    return fromAttr;
  }

  const raw = href.trim();

  if (!raw) {
    return "";
  }

  const lower = raw.toLowerCase();

  if (kind === "phone" && lower.startsWith("tel:")) {
    return raw.slice(4);
  }

  if (kind === "email" && lower.startsWith("mailto:")) {
    return raw.slice(7);
  }

  if (kind === "whatsapp") {
    const wa = /^https?:\/\/wa\.me\//i;

    if (wa.test(raw)) {
      return raw.replace(wa, "");
    }
  }

  return raw;
}

export function parseMailContactTable(el: HTMLElement, blockId: string): MailContactBlock | null {
  if (el.getAttribute("data-mail-contact") !== "1") {
    return null;
  }

  const cells = Array.from(el.querySelectorAll("td[data-mail-contact-kind]"));
  const found: MailContactItem[] = [];

  for (const cell of cells) {
    const kindRaw = (cell.getAttribute("data-mail-contact-kind") || "").trim();

    if (!isMailContactKind(kindRaw)) {
      continue;
    }

    const attrValue = cell.getAttribute("data-mail-contact-value") || "";
    const anchor = cell.querySelector("a");
    const href = anchor?.getAttribute("href") || "";

    found.push({
      id: newContactItemId(),
      kind: kindRaw,
      enabled: true,
      value: valueFromStoredHref(kindRaw, href, attrValue),
    });
  }

  return { id: blockId, type: "contact", items: ensureMailContactItems(found) };
}
